/**
 * CaptionControl - Component for rendering captions in tile-footer slot
 *
 * This component extracts and renders captions from media metadata.
 * It's used as a control in the tile-footer slot to display captions below the media.
 */

import React from 'react';

import {makeCaptions} from '../../../util/media';
import {GridCell} from '../layout/types';

export type CaptionControlProps = {
  cell: GridCell;
};

/**
 * Renders captions for a media tile.
 *
 * @param props - CaptionControlProps
 * @returns Rendered captions or null if no captions
 */
export const CaptionControl: React.FC<CaptionControlProps> = ({cell}) => {
  if (cell.isEmpty) {
    return null;
  }

  const {run, mediaKey, mediaIndex, step, tileSpec} = cell;
  const mediaSliderKey = tileSpec.mediaSliderKey ?? '_step';
  const mediaSliderKeyValue = tileSpec.mediaSliderKeyValue;

  // Find the history row that matches the correct key
  // If we have a mediaSliderKeyValue (e.g., timestamp), use that to match on the slider key
  // Otherwise, use step to match on _step
  let historyRow;
  if (
    mediaSliderKeyValue != null &&
    mediaSliderKey &&
    mediaSliderKey !== '_step'
  ) {
    historyRow = run.history?.find(
      hr => mediaKey in hr && hr[mediaSliderKey] === mediaSliderKeyValue
    );
  } else {
    historyRow = run.history?.find(hr => mediaKey in hr && hr._step === step);
  }

  if (!historyRow) {
    return null;
  }

  // Get metadata directly from the correct history row
  const metadata = historyRow[mediaKey];

  if (!metadata) {
    return null;
  }

  const captions = makeCaptions(metadata, mediaIndex);

  if (captions.length === 0) {
    return null;
  }

  return (
    <div className="image-card-caption break-words w-full overflow-x-hidden">
      {captions}
    </div>
  );
};
